import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardSummaryStore } from '@lib/store';
import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { Item } from './item';
import { View } from '@widgets';
import { Text } from '@shared/widgets';

interface CurrenciesProps {
  className?: string;
}

export const DashboardCurrencies = (props: CurrenciesProps) => {
  const dashboardSummaryStore = useUnit($dashboardSummaryStore.store);

  const { t } = useTranslation('dashboard');

  useEffect(() => {
    $dashboardSummaryStore.events.getSummaries();
    return () => $dashboardSummaryStore.events.clear();
  }, []);

  return (
    <section
      className={cn(
        'flex flex-col gap-6',
        'max-md:gap-3',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:border dark:border-gray-black dark:bg-soft-black',
        props.className,
      )}
    >
      <Text tag='h2' weight={PaletteUtils.isVcfcPalette ? 'semibold' : 'medium'} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
        {t('card.currency.title', { Count: PaletteUtils.isInsigniaPalette ? `(${dashboardSummaryStore.data.length})` : '' })}
      </Text>
      <View
        loading={dashboardSummaryStore.loading}
        error={dashboardSummaryStore.error}
        noData={!dashboardSummaryStore.data.length}
        onRefresh={() => $dashboardSummaryStore.events.getSummaries()}
      >
        <div className='flex flex-col gap-4'>
          {dashboardSummaryStore.data.map((item, idx) => (
            <div key={idx} className='flex items-center gap-12 bg-white dark:bg-soft-black'>
              {PaletteUtils.isInsigniaPalette && (
                <div
                  className={cn(
                    'relative h-[90px] w-[92px] shrink-0 grow-0 basis-auto overflow-hidden bg-soft-black dark:bg-slate',
                    'before:absolute before:left-1/2 before:top-1/2 before:-ml-3 before:mt-[6px] before:-translate-x-1/2 before:-translate-y-1/2 before:text-[47px] before:leading-[1] before:text-primary before:content-[attr(data-icon)]',
                    'after:absolute after:-bottom-[17px] after:right-[8px] after:text-[62px] after:leading-[1] after:text-primary after:opacity-[0.05] after:content-[attr(data-icon)]',
                  )}
                  data-icon={CurrencyUtils.getSymbolByCode(item.currency)}
                >
                  <div className='absolute bottom-[-2px] left-[calc(100%-2px)] h-[150%] w-1/2 origin-bottom-left rotate-[-20deg] border-l border-primary bg-white dark:bg-soft-black' />
                </div>
              )}
              {PaletteUtils.isVcfcPalette && (
                <Text className='flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-primary' size='3xl' color='white'>
                  {CurrencyUtils.getSymbolByCode(item.currency)}
                </Text>
              )}
              <ul
                className={cn(
                  'flex w-full items-center justify-around gap-6',
                  'max-lg:grid max-lg:grid-cols-1 max-lg:gap-3',
                  PaletteUtils.isInsigniaPalette && 'py-4',
                )}
              >
                <Item label={t('card.currency.fields.accounts')} value={item.accounts} />
                <Item label={t('card.currency.fields.cards')} value={item.cards} />
                <Item
                  label={t('card.currency.fields.availableBalance')}
                  value={AmountUtils.transformToAmountWithCurrency(item.availableBalance, item.currency)}
                />
                <Item label={t('card.currency.fields.amountDue')} value={AmountUtils.transformToAmountWithCurrency(item.amountDue, item.currency)} />
                <Item
                  label={t('card.currency.fields.totalLimit')}
                  value={AmountUtils.transformToAmountWithCurrency(item.totalLimit, item.currency)}
                />
              </ul>
            </div>
          ))}
        </div>
      </View>
    </section>
  );
};
